import { useCallback, MouseEvent, useEffect } from 'react';
import { Field, useField, useFormikContext } from 'formik';

import styled from 'styled-components';
import { Alert, Button, MenuItem, Typography } from '@mui/material';

import { FormRowsDivider, InputField } from '../../../fields';
import { api } from '../../../../api';
import { ApiError } from '../../../errors';
import { SendTestLead } from '../../send-test-lead';

export type AmoCRMSettings = {
  clientId: string;
  authCode: string;
  subdomain: string;
  secret: string;
  accessToken: string;
  refreshToken: string;
  pipelineId: number;
  statusId: number;
};

export const AmoCRMIntegration = () => {
  const { setFieldValue } = useFormikContext();
  const [{ value: amoCRMIntegration }] = useField<AmoCRMSettings>('campaign.amoCRMIntegration');
  const [{ value: campaignId }] = useField('campaign.id');

  const [activate, activateResult] = api.useActivateAmoCRMMutation();
  const [getPipelines, pipelines] = api.useLazyGetAmoCRMPipelinesQuery();
  const [getPipelineStatuses, pipelineStatuses] = api.useLazyGetAmoCRMPipelineStatusesQuery();

  const { subdomain, clientId, secret, authCode, pipelineId, accessToken } = amoCRMIntegration || {};

  const integrationActive =
    amoCRMIntegration &&
    amoCRMIntegration.subdomain &&
    amoCRMIntegration.clientId &&
    amoCRMIntegration.secret &&
    amoCRMIntegration.authCode;

  const handleBlurAmoCRMSubdomain = useCallback(() => {
    const amoSubdomain = amoCRMIntegration?.subdomain;

    if (!amoSubdomain) {
      return;
    }

    try {
      const url = new URL(amoSubdomain);

      const [subdomain] = url.host.split('.');

      if (subdomain) {
        setFieldValue('campaign.amoCRMIntegration.subdomain', subdomain);
      }
    } catch (err) {
      //
    }
  }, [amoCRMIntegration?.subdomain, setFieldValue]);

  const fetchPipelines = useCallback(() => getPipelines({ campaignId }), [campaignId, getPipelines]);

  const fetchPipelineStatuses = useCallback(
    (pipelineId) => getPipelineStatuses({ campaignId, pipelineId }),
    [campaignId, getPipelineStatuses]
  );

  const handleActivateClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      activate({ clientId, subdomain, authCode, secret, campaignId });
    },
    [activate, authCode, campaignId, clientId, secret, subdomain]
  );

  const handlePipelineChange = useCallback(() => {
    setFieldValue('campaign.amoCRMIntegration.statusId', undefined);
  }, [setFieldValue]);

  useEffect(() => {
    if (activateResult.isSuccess) {
      setFieldValue('campaign.amoCRMIntegration.accessToken', activateResult.data.accessToken);
      setFieldValue('campaign.amoCRMIntegration.refreshToken', activateResult.data.refreshToken);
      setFieldValue('campaign.amoCRMIntegration.tagId', activateResult.data.tagId);
    }
  }, [activateResult.data, activateResult.isSuccess, setFieldValue]);

  useEffect(() => {
    if (integrationActive && accessToken) {
      fetchPipelines();
    }
  }, [integrationActive, fetchPipelines, accessToken]);

  useEffect(() => {
    if (integrationActive && pipelineId && pipelines.isSuccess) {
      fetchPipelineStatuses(pipelineId);
    }
  }, [integrationActive, fetchPipelineStatuses, pipelineId, pipelines.isSuccess]);

  return (
    <Wrapper>
      <FormRowsDivider />

      <Typography sx={{ fontWeight: 'bold', marginBottom: '12px' }}>AmoCRM</Typography>

      <Alert severity="info" variant="filled" sx={{ marginBottom: '24px' }}>
        Чтобы отправлять заявки в AmoCRM, вам нужно создать новую внешнюю интегрцию у себя в AmoCRM. Ссылку для
        перенаправления укажите https://api.embedgames.app/integration/amocrm
        <Button
          href="https://blog.embedgames.app/articles/integraciya-s-amocrm"
          target="_blank"
          sx={{ marginLeft: '-8px' }}
        >
          Подробнее
        </Button>
      </Alert>

      <SubdomainInputWrap>
        <Field
          name="campaign.amoCRMIntegration.subdomain"
          component={InputField}
          label="Поддомен вашей CRM"
          placeholder="my-company"
          className="form-field"
          submitOnBlur={false}
          onBlur={handleBlurAmoCRMSubdomain}
          helperText={
            <>
              Если адрес вашей CRM https://company.amocrm.ru, <br />
              то поддомен это просто <b>company</b>
            </>
          }
        />

        <Amo>.amocrm.ru</Amo>
      </SubdomainInputWrap>

      <Field
        name="campaign.amoCRMIntegration.clientId"
        component={InputField}
        label="ID интеграции"
        className="form-field"
        submitOnBlur={false}
      />

      <Field
        name="campaign.amoCRMIntegration.secret"
        component={InputField}
        label="Секретный ключ"
        className="form-field"
        submitOnBlur={false}
        type="password"
      />

      <Field
        name="campaign.amoCRMIntegration.authCode"
        component={InputField}
        label="Код авторизации"
        className="form-field"
        submitOnBlur={false}
        type="password"
      />

      <Button
        sx={{ margin: '4px 0 20px' }}
        onClick={handleActivateClick}
        disabled={!integrationActive || activateResult.isLoading}
      >
        Активировать
      </Button>

      {activateResult.isError && (
        <Alert severity="error" variant="filled" sx={{ margin: '8px 0 20px' }}>
          <ApiError error={activateResult.error} />
        </Alert>
      )}

      {integrationActive && accessToken && (
        <Row>
          <Field
            name="campaign.amoCRMIntegration.pipelineId"
            component={InputField}
            label="Воронка"
            className="form-field"
            disabled={pipelines.isLoading}
            select
            onChange={handlePipelineChange}
          >
            {pipelines.data?.pipelines.map((pipeline) => (
              <MenuItem key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </MenuItem>
            ))}
          </Field>

          <Field
            name="campaign.amoCRMIntegration.statusId"
            component={InputField}
            label="Статус"
            className="form-field"
            select
            disabled={pipelineStatuses.isLoading || pipelines.isLoading}
          >
            {pipelineStatuses.data?.statuses.map((pipelineStatus) => (
              <MenuItem key={pipelineStatus.id} value={pipelineStatus.id}>
                {pipelineStatus.name}
              </MenuItem>
            ))}
          </Field>
        </Row>
      )}

      {pipelines.isError && <ApiError error={pipelines.error} withMargin />}
      {pipelineId && <SendTestLead integrationId="amocrm" campaignId={campaignId} />}
    </Wrapper>
  );
};

const SubdomainInputWrap = styled.div`
  display: flex;
`;

const Amo = styled.div`
  margin-top: 16px;
  margin-left: 4px;
  opacity: 0.6;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .form-field {
    margin-bottom: 12px;
    width: 335px;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
`;
